import { ColumnDef } from '@tanstack/react-table'
import dayjs from 'dayjs'
import RelativeTime from 'dayjs/plugin/relativeTime'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  CircleSlash2Icon,
  ExternalLink,
  Eye,
  Loader2,
  LockKeyhole,
  RefreshCw,
} from 'lucide-react'
import { difficultiesLabels } from 'app/frontend/types/custom/Difficulty'
import { gameVersionHas } from 'app/frontend/types/custom/GameVersion'
import EventRaidReportsBadge from '../TeamEvent/components/EventRaidReportsBadge'
import EventAvatar from 'app/frontend/base/components/EventAvatar'
import { eventInstant } from 'app/frontend/base/helpers/eventInstant'
import { Link } from 'react-router-dom'
import type { MouseEvent } from 'react'

dayjs.extend(RelativeTime)

const shouldShowRaidReportBadge = (event: wowaudit.EventBasicReference) =>
  eventInstant(event.startsAt, event.timezone).isBefore(dayjs())

type EventTitleColumnOptions<
  TEvent extends wowaudit.EventBasicReference | wowaudit.EventReference,
> = {
  hrefForEvent?: (event: TEvent) => string | undefined
  isExternalEvent?: (event: TEvent) => boolean
}

export const eventTitleColumn = <
  TEvent extends wowaudit.EventBasicReference | wowaudit.EventReference,
>(
  team?: wowaudit.Team,
  options?: EventTitleColumnOptions<TEvent>,
): ColumnDef<TEvent> => ({
  id: 'title',
  header: 'Event',
  cell: ({ row }) => {
    const event = row.original
    const status = typeof event.status === 'string' ? event.status : undefined
    const activityType =
      typeof event.activityType === 'string' ? event.activityType : undefined
    const primaryDifficulty =
      typeof event.primaryDifficulty === 'string'
        ? event.primaryDifficulty
        : undefined
    const optional = event.optional === true
    const href =
      options?.hrefForEvent?.(event) ??
      (team ? `${team.path}/events/${event.id}` : '')
    const isExternal = options?.isExternalEvent?.(event) ?? false
    const titleContent = (
      <>
        <EventAvatar
          eventReference={event as unknown as wowaudit.EventReference}
          className='h-8 w-8 rounded-full'
        />
        <div className='min-w-0 leading-tight'>
          <div className='flex items-center gap-1.5 min-w-0'>
            <span className='truncate'>{event.title || 'Event'}</span>
            {isExternal ? (
              <ExternalLink className='h-3.5 w-3.5 shrink-0 text-muted-foreground' />
            ) : null}
          </div>
          <div className='flex items-center gap-1 text-xs text-muted-foreground'>
            {status === 'locked' ? (
              <LockKeyhole className='h-3 w-3 text-warning-foreground' />
            ) : null}
            {status === 'cancelled' ? (
              <CircleSlash2Icon className='h-3 w-3 text-destructive-foreground' />
            ) : null}
            {activityType ? (
              <span className='capitalize'>{activityType}</span>
            ) : null}
            {primaryDifficulty &&
            activityType === 'raids' &&
            gameVersionHas('difficulties') ? (
              <span>
                -{' '}
                {
                  difficultiesLabels[
                    primaryDifficulty as keyof typeof difficultiesLabels
                  ]
                }
              </span>
            ) : null}
            {optional ? (
              <span className='text-xs text-info-foreground'>(optional)</span>
            ) : null}
          </div>
        </div>
      </>
    )
    const className = 'flex items-center gap-2'
    const stopRowClick = (clickEvent: MouseEvent) => {
      clickEvent.stopPropagation()
    }

    if (isExternal) {
      return (
        <a href={href} className={className} onClick={stopRowClick}>
          {titleContent}
        </a>
      )
    }

    return (
      <Link to={href} className={className} onClick={stopRowClick}>
        {titleContent}
      </Link>
    )
  },
})

export const eventDateColumn = <
  TEvent extends wowaudit.EventBasicReference,
>(): ColumnDef<TEvent> => ({
  id: 'date',
  header: 'Date',
  cell: ({ row }) => {
    const event = row.original
    return (
      <div className='leading-tight whitespace-nowrap'>
        <div>{eventInstant(event.startsAt, event.timezone).fromNow()}</div>
        <div className='text-xs text-muted-foreground'>
          {event.date ? dayjs(event.date).format('ddd, D MMM YYYY') : '-'}
        </div>
      </div>
    )
  },
})

export const eventTimeColumn = <
  TEvent extends wowaudit.EventBasicReference,
>(): ColumnDef<TEvent> => ({
  id: 'time',
  header: 'Time',
  cell: ({ row }) => {
    const event = row.original
    return (
      <div className='whitespace-nowrap'>
        {event.startTime || '--:--'} - {event.finishTime || '--:--'}
      </div>
    )
  },
})

type EventRaidReportsColumnOptions = {
  raidReportsByEventId: Record<string, number[]>
  isLoading: boolean
  onOpen: (eventId: number) => void
  headerLabel?: string
  pullCountByEventId?: Record<string, number>
  encounterName?: string
}

type EventReportActionsColumnOptions = {
  onView: (eventId: number) => void
  onSync?: (eventId: number) => void
  syncingEventId?: number | null
  syncLocked?: boolean
}

export const eventRaidReportsColumn = <
  TEvent extends wowaudit.EventBasicReference,
>({
  raidReportsByEventId,
  isLoading,
  onOpen,
  headerLabel = 'Report',
  pullCountByEventId,
  encounterName,
}: EventRaidReportsColumnOptions): ColumnDef<TEvent> => ({
  id: 'raid_reports',
  header: headerLabel,
  cell: ({ row }) => {
    const event = row.original
    if (!shouldShowRaidReportBadge(event)) return null

    const hasLinkedReport =
      (raidReportsByEventId[event.id.toString()]?.length || 0) > 0
    const pullCount = pullCountByEventId?.[event.id.toString()]

    return (
      <div
        className='flex items-center gap-2'
        onClick={(clickEvent) => {
          clickEvent.stopPropagation()
        }}
      >
        <EventRaidReportsBadge
          isLoading={isLoading}
          hasLinkedReport={hasLinkedReport}
          onClick={() => onOpen(event.id)}
        />
        {pullCountByEventId && hasLinkedReport ? (
          <div className='leading-tight'>
            {encounterName ? (
              <div className='text-xs text-muted-foreground'>
                {encounterName}
              </div>
            ) : null}
            <div
              className={`text-xs ${
                encounterName && (pullCount ?? 0) > 0
                  ? ''
                  : 'text-muted-foreground'
              }`}
            >
              {pullCount ?? 0} {(pullCount ?? 0) === 1 ? 'pull' : 'pulls'}
            </div>
          </div>
        ) : null}
      </div>
    )
  },
})

export const eventReportActionsColumn = <
  TEvent extends wowaudit.EventBasicReference,
>({
  onView,
  onSync,
  syncingEventId,
  syncLocked = false,
}: EventReportActionsColumnOptions): ColumnDef<TEvent> => ({
  id: 'report_actions',
  header: '',
  cell: ({ row }) => {
    const event = row.original
    if (!shouldShowRaidReportBadge(event)) return null

    const syncing = syncingEventId === event.id

    return (
      <div
        className='flex items-center gap-1'
        onClick={(clickEvent) => {
          clickEvent.stopPropagation()
        }}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type='button'
              size='sm'
              variant='outline'
              className='h-8 w-8 p-0'
              onClick={() => onView(event.id)}
            >
              <Eye className='h-4 w-4' />
            </Button>
          </TooltipTrigger>
          <TooltipContent>View reports</TooltipContent>
        </Tooltip>
        {onSync ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type='button'
                size='sm'
                variant='outline'
                className='h-8 w-8 p-0'
                disabled={syncing || syncLocked}
                onClick={() => onSync(event.id)}
              >
                {syncing ? (
                  <Loader2 className='h-4 w-4 animate-spin' />
                ) : (
                  <RefreshCw className='h-4 w-4' />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>Sync now</TooltipContent>
          </Tooltip>
        ) : null}
      </div>
    )
  },
})

export const eventRaidReportsSummaryBadge = ({
  linkedCount,
  totalCount,
  onClick,
}: {
  linkedCount: number
  totalCount: number
  onClick: () => void
}) => (
  <button type='button' onClick={onClick}>
    <Badge variant='outline' className='text-xs h-5'>
      {linkedCount} / {totalCount}
    </Badge>
  </button>
)
